import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Radio,
  Server,
  Smartphone,
  Activity,
  Play,
  Square,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Send,
  Inbox,
  TrendingUp,
  Clock,
  Users,
  Zap
} from 'lucide-react';
import { cn } from '../ui/utils';
import { toast } from 'sonner';
import { useBroadcastStore } from '../../stores/broadcastStore';
import { useDeviceStatus } from '../../stores/systemStore';
import { useConnectedDevice } from '../../stores/deviceStore';
import { mockDataGenerator } from '../../utils/mockDataGenerator';
import { useTranslation } from '../../locales';
import { useLanguageStore } from '../../stores/languageStore';

export function StreamProcess() {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const tSp = t.streamProcess;

  const [serverUrl, setServerUrl] = useState(
    import.meta.env.VITE_BROADCAST_SERVER_URL || 'https://broadcast-sdk.looxidlabs.com'
  );
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationInterval, setSimulationInterval] = useState<NodeJS.Timeout | null>(null);
  const [simulationTimeout, setSimulationTimeout] = useState<NodeJS.Timeout | null>(null);

  // Broadcast Store
  const {
    isConnected,
    isSubscribing,
    deviceId: broadcastDeviceId,
    stats,
    receivedMessages,
    lastError,
    connect,
    disconnect,
    publish,
    startSubscription,
    stopSubscription,
    checkHealth,
    getServerStats,
    clearMessages,
    clearError,
    updateDeviceId
  } = useBroadcastStore();

  // Device Store
  const { isConnected: deviceConnected, currentDeviceId, deviceName } = useDeviceStatus();
  const connectedDevice = useConnectedDevice();

  // 서버 Health 상태
  const [serverHealth, setServerHealth] = useState<any>(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);

  // 서버 통계
  const [serverStats, setServerStats] = useState<any[]>([]);

  // Device ID가 변경되면 Broadcast Store 업데이트
  useEffect(() => {
    if (currentDeviceId) {
      updateDeviceId(currentDeviceId);
    }
  }, [currentDeviceId, updateDeviceId]);

  // 서버 연결 핸들러
  const handleConnect = async () => {
    // Device ID 결정: 실제 디바이스 ID 또는 Mock ID
    const deviceId = currentDeviceId || `mock-${Date.now()}`;

    // 실측 생체 데이터 외부 전송 동의 게이트 (WS-C)
    // 브로드캐스트 서버 연결은 EEG·PPG·ACC 원시 데이터를 외부로 전송하므로
    // 최초 1회 명시적 동의를 받고 localStorage에 보관한다.
    // (SystemControlService의 publish 가드가 동일 키를 확인)
    if (localStorage.getItem('broadcast-consent') !== 'granted') {
      const consentMessage = currentLanguage === 'ko'
        ? '브로드캐스트 서버에 연결하면 실시간 생체 데이터(EEG·PPG·ACC)가 외부 서버로 전송됩니다. 계속하시겠습니까?'
        : 'Connecting to the broadcast server will transmit real-time biometric data (EEG/PPG/ACC) to an external server. Continue?';

      if (!window.confirm(consentMessage)) {
        return;
      }
      localStorage.setItem('broadcast-consent', 'granted');
    }

    try {
      await connect(serverUrl, deviceId);
      toast.success(tSp.connectedToast, {
        description: currentDeviceId 
          ? `Device: ${currentDeviceId}` 
          : tSp.connectedMockDesc
      });
    } catch (error) {
      toast.error(tSp.connectFailed, {
        description: error instanceof Error ? error.message : tSp.unknownError
      });
    }
  };

  // 서버 연결 해제
  const handleDisconnect = () => {
    disconnect();
    stopSimulation();
    toast.info(tSp.disconnectedToast);
  };

  // Health Check
  const handleHealthCheck = async () => {
    setIsCheckingHealth(true);
    try {
      const health = await checkHealth();
      setServerHealth(health);
      toast.success(tSp.healthOk);
    } catch (error) {
      toast.error(tSp.healthFail);
    } finally {
      setIsCheckingHealth(false);
    }
  };

  // 서버 통계 조회
  const handleGetStats = async () => {
    try {
      const stats = await getServerStats();
      setServerStats(stats);
      toast.success(tSp.statsOk);
    } catch (error) {
      toast.error(tSp.statsFail);
    }
  };

  // Mock 데이터 전송 테스트
  const sendTestData = async (type: 'eeg-raw' | 'ppg-raw' | 'acc-raw' | 'analysis' | 'combined') => {
    if (!isConnected) {
      toast.error(tSp.connectFirst);
      return;
    }

    try {
      let payload;
      
      switch (type) {
        case 'eeg-raw':
          payload = {
            version: '1.0' as const,
            eegRaw: mockDataGenerator.generateEEGRaw(250)
          };
          break;
        case 'ppg-raw':
          payload = {
            version: '1.0' as const,
            ppgRaw: mockDataGenerator.generatePPGRaw(50)
          };
          break;
        case 'acc-raw':
          payload = {
            version: '1.0' as const,
            accRaw: mockDataGenerator.generateACCRaw(30)
          };
          break;
        case 'analysis':
          payload = mockDataGenerator.generateAnalysisOnly();
          break;
        case 'combined':
          payload = mockDataGenerator.generateCombinedPayload();
          break;
      }

      const result = await publish(payload);
      console.log('[Publish] result:', result, 'deviceId:', broadcastDeviceId);
      toast.success(tSp.dataSent, {
        description: tSp.dataSentDesc
          .replace('{count}', String(result.subscribers))
          .replace('{time}', String(result.responseTime))
      });
    } catch (error) {
      toast.error(tSp.dataSendFail);
    }
  };

  // 연속 스트림 시뮬레이션
  const startSimulation = () => {
    if (!isConnected) {
      toast.error(tSp.connectFirst);
      return;
    }

    setIsSimulating(true);
    
    const interval = setInterval(async () => {
      try {
        const payload = mockDataGenerator.generateCombinedPayload();
        await publish(payload);
      } catch (error) {
        console.error('Simulation publish error:', error);
      }
    }, 100); // 100ms마다 (초당 10회)

    setSimulationInterval(interval);
    
    // 10초 후 자동 중지
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setSimulationInterval(null);
      setSimulationTimeout(null);
      setIsSimulating(false);
      toast.info(tSp.simulationComplete);
    }, 10000);
    
    setSimulationTimeout(timeout);
    toast.success(tSp.simulationStarted);
  };

  const stopSimulation = () => {
    // interval 정리
    if (simulationInterval) {
      clearInterval(simulationInterval);
      setSimulationInterval(null);
    }
    
    // timeout 정리
    if (simulationTimeout) {
      clearTimeout(simulationTimeout);
      setSimulationTimeout(null);
    }
    
    setIsSimulating(false);
    toast.info(tSp.simulationStopped);
  };

  // 구독 시작/중지
  const toggleSubscription = () => {
    if (isSubscribing) {
      stopSubscription();
      toast.info(tSp.subscriptionStopped);
    } else {
      startSubscription();
      toast.success(tSp.subscriptionStarted);
    }
  };

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (simulationInterval) {
        clearInterval(simulationInterval);
      }
      if (simulationTimeout) {
        clearTimeout(simulationTimeout);
      }
    };
  }, [simulationInterval, simulationTimeout]);

  return (
    <div className="flex-1 overflow-auto p-6 bg-background text-foreground">
      <div className="max-w-7xl mx-auto space-y-6 pb-20">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-medium text-foreground">{tSp.title}</h1>
            <p className="text-muted-foreground">{tSp.subtitle}</p>
          </div>
        </div>

        {/* 에러 표시 */}
        {lastError && (
          <Alert className="border-red-500 bg-red-50 dark:bg-red-900/20">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertDescription className="text-red-700 dark:text-red-300">
              {lastError}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearError}
                className="ml-2 h-6 text-xs"
              >
                {tSp.close}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 좌측: 상태 정보 */}
          <div className="lg:col-span-2 space-y-6">
            {/* 서버 연결 상태 카드 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  {tSp.broadcastServer}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{tSp.serverUrl}</label>
                  <div className="flex gap-2">
                    <Input
                      value={serverUrl}
                      onChange={(e) => setServerUrl(e.target.value)}
                      placeholder="http://localhost:8080"
                      disabled={isConnected}
                    />
                    {!isConnected ? (
                      <Button onClick={handleConnect}>
                        <Radio className="w-4 h-4 mr-2" />
                        {tSp.connect}
                      </Button>
                    ) : (
                      <Button onClick={handleDisconnect} variant="destructive">
                        <XCircle className="w-4 h-4 mr-2" />
                        {tSp.disconnect}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-3 h-3 rounded-full",
                      isConnected ? "bg-green-500 animate-pulse" : "bg-gray-400"
                    )} />
                    <span className="text-sm font-medium">
                      {isConnected ? tSp.connected : tSp.notConnected}
                    </span>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleHealthCheck}
                    disabled={!isConnected || isCheckingHealth}
                  >
                    <RefreshCw className={cn(
                      "w-4 h-4 mr-2",
                      isCheckingHealth && "animate-spin"
                    )} />
                    {tSp.healthCheck}
                  </Button>
                </div>

                {serverHealth && (
                  <div className="p-3 bg-muted/50 rounded-lg space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{tSp.status}</span>
                      <span className="font-medium text-green-600">{serverHealth.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{tSp.uptime}</span>
                      <span className="font-medium">{Math.floor(serverHealth.uptime)}{tSp.seconds}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{tSp.activeDevice}</span>
                      <span className="font-medium">{serverHealth.devices}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{tSp.totalConnections}</span>
                      <span className="font-medium">{serverHealth.totalConnections}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 디바이스 상태 카드 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  {tSp.deviceStatus}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deviceConnected ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{tSp.deviceName}</span>
                      <span className="text-sm font-medium">
                        {connectedDevice?.device?.name || deviceName || 'Unknown Device'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{tSp.deviceId}</span>
                      <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                        {currentDeviceId || 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{tSp.connectionStatus}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        <span className="text-sm font-medium text-green-600">{tSp.connected}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{tSp.battery}</span>
                      <span className="text-sm font-medium">
                        {connectedDevice?.battery?.level || 0}%
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Alert className="mb-3">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {tSp.noDeviceAlert}
                      </AlertDescription>
                    </Alert>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{tSp.mode}</span>
                      <Badge variant="outline" className="text-orange-600">
                        {tSp.mockMode}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{tSp.broadcastId}</span>
                      <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                        {broadcastDeviceId || 'Not connected'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground pt-2">
                      {tSp.mockHint}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 스트리밍 제어 카드 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  {tSp.streamingControl}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Send className="w-4 h-4 text-blue-500" />
                      <span className="text-xs text-muted-foreground">{tSp.sent}</span>
                    </div>
                    <p className="text-2xl font-bold">{stats.messagesSent}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Inbox className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-muted-foreground">{tSp.received}</span>
                    </div>
                    <p className="text-2xl font-bold">{stats.messagesReceived}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-purple-500" />
                      <span className="text-xs text-muted-foreground">{tSp.subscribers}</span>
                    </div>
                    <p className="text-2xl font-bold">{stats.subscribers}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-orange-500" />
                      <span className="text-xs text-muted-foreground">{tSp.avgResponse}</span>
                    </div>
                    <p className="text-2xl font-bold">
                      {stats.averageResponseTime.toFixed(0)}ms
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={toggleSubscription}
                    disabled={!isConnected}
                    variant={isSubscribing ? "destructive" : "default"}
                    className="flex-1"
                  >
                    {isSubscribing ? (
                      <>
                        <Square className="w-4 h-4 mr-2" />
                        {tSp.stopSubscription}
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        {tSp.startSubscription}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleGetStats}
                    disabled={!isConnected}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    {tSp.viewStats}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 우측: 시뮬레이션 */}
          <div className="space-y-6">
            {/* 시뮬레이션 섹션 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  {tSp.mockSimulation}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground mb-3">
                    {tSp.mockSimulationDesc}
                  </p>
                  
                  <Button
                    onClick={() => sendTestData('eeg-raw')}
                    disabled={!isConnected}
                    variant="outline"
                    className="w-full justify-start"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {tSp.eegRawLabel}
                  </Button>
                  
                  <Button
                    onClick={() => sendTestData('ppg-raw')}
                    disabled={!isConnected}
                    variant="outline"
                    className="w-full justify-start"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {tSp.ppgRawLabel}
                  </Button>
                  
                  <Button
                    onClick={() => sendTestData('acc-raw')}
                    disabled={!isConnected}
                    variant="outline"
                    className="w-full justify-start"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {tSp.accRawLabel}
                  </Button>
                  
                  <Button
                    onClick={() => sendTestData('analysis')}
                    disabled={!isConnected}
                    variant="outline"
                    className="w-full justify-start"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {tSp.analysisMetricsLabel}
                  </Button>
                  
                  <Button
                    onClick={() => sendTestData('combined')}
                    disabled={!isConnected}
                    variant="outline"
                    className="w-full justify-start"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {tSp.combinedDataLabel}
                  </Button>

                  <div className="pt-3 border-t">
                    {!isSimulating ? (
                      <Button
                        onClick={startSimulation}
                        disabled={!isConnected}
                        className="w-full bg-blue-500 hover:bg-blue-600"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {tSp.continuousStreamLabel}
                      </Button>
                    ) : (
                      <Button
                        onClick={stopSimulation}
                        variant="destructive"
                        className="w-full"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        {tSp.stopSimulationLabel}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 서버 통계 카드 */}
            {serverStats.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    {tSp.serverStats}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {serverStats.map((stat) => (
                      <div
                        key={stat.deviceId}
                        className="p-2 bg-muted/50 rounded space-y-1"
                      >
                        <div className="font-mono text-xs truncate">{stat.deviceId}</div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{tSp.subscribers}</span>
                          <span className="font-medium">{stat.subscribers}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{tSp.messages}</span>
                          <span className="font-medium">{stat.messageCount}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* 실시간 모니터링 - 메시지 로그 */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Inbox className="w-5 h-5" />
              {tSp.messagesLabel} ({receivedMessages.length})
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={clearMessages}
              disabled={receivedMessages.length === 0}
            >
              {tSp.clear}
            </Button>
          </CardHeader>
          <CardContent>
            {receivedMessages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Inbox className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{tSp.noMessages}</p>
                <p className="text-sm">{tSp.noMessagesDesc}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {receivedMessages.slice().reverse().map((msg) => (
                  <div
                    key={msg.id}
                    className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {msg.deviceId}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {msg.types.map((type) => (
                          <Badge key={type} variant="secondary" className="text-xs">
                            {type}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-xs font-mono text-muted-foreground">
                      {JSON.stringify(msg.payload).substring(0, 100)}...
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
